import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, AlertCircle, Gift, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import '../styles/advertorial.css';

const Home = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 1,
    minutes: 50,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        let { days, hours, minutes, seconds } = prevTime;
        
        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  return (
    <div className="advertorial-page">
      <Helmet>
        <title>EverSock Compression Socks - Instant Relief for Leg Swelling, Fatigue & Pain</title>
        <meta 
          name="description" 
          content="Over 50,000+ customers chose EverSock compression socks for instant relief from leg swelling, fatigue and pain. 20-30mmHg graduated compression for all-day support." 
        />
        <meta 
          name="keywords" 
          content="compression socks, leg swelling, varicose veins, edema, circulation, leg pain, travel socks, graduated compression" 
        />
        <meta property="og:title" content="EverSock Compression Socks - Instant Relief for Leg Swelling, Fatigue & Pain" />
        <meta 
          property="og:description" 
          content="Over 50,000+ customers chose EverSock compression socks for instant relief from leg swelling, fatigue and pain." 
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://yourdomain.com/" />
      </Helmet>
      
      {/* Top Banner with Timer */}
      <div className="top-banner">
        <div className="banner-content">
          <span className="sale-text">WINTER SALE - UP TO 50% OFF</span>
          <div className="countdown-timer">
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.days)}</span>
              <span className="time-label">DAYS</span>
            </div>
            <span className="separator">:</span>
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.hours)}</span>
              <span className="time-label">HRS</span>
            </div>
            <span className="separator">:</span>
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.minutes)}</span>
              <span className="time-label">MIN</span>
            </div>
            <span className="separator">:</span>
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.seconds)}</span>
              <span className="time-label">SEC</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar"></div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <h1 className="hero-title">
            Tired of Leg Swelling, Fatigue & Pain? <br />
            <span className="hero-highlight">These Compression Socks Deliver Instant Relief & All-Day Support</span>
          </h1>
          <p className="hero-description">
            Over 1 in 3 adults experience swelling, heaviness, or discomfort in their legs daily, 
            especially those who stand, sit, or travel for long hours. But most compression socks 
            are either too tight, uncomfortable, or simply don't work.
          </p>
          <div className="note-box">
            <strong>Note:</strong> Read this BEFORE buying another pair of generic compression socks.
          </div>

          {/* Nurse Expert Box */}
          <div className="expert-box">
            <div className="expert-image">
              <img 
                src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876ab99db03ef0e66af3b87_Bethany%20Adjusted.webp" 
                alt="Nurse Bethany C." 
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="expert-info">
              <div className="verified-badge">
                <img 
                  src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/68778ae1f00addecf7b56d33_verified.webp" 
                  alt="Verified" 
                  className="verified-icon" 
                  loading="eager"
                  decoding="async"
                />
                <span className="verified-text">Verified</span>
              </div>
              <h3 className="expert-name">Nurse Bethany C.</h3>
              <p className="expert-credentials">Certified Nurse with more than 15 years of comprehensive experience in healthcare.</p>
            </div>
          </div>
        </section>

        {/* Feature Section 1 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876accfc135fc37d96cf8c8_IMAGE%201%20-%20PRE%20LANDER.webp" 
              alt="Compression socks relief" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">1. Slip On Relief Instantly - Swelling Down, Circulation Up</h2>
            <p className="feature-description">
              Most socks just squeeze without results. EverSock™ compression socks deliver 20-30mmHg 
              of true graduated compression, meaning they support your veins and muscles exactly where 
              needed — firm at the ankle and lighter toward the calf. This helps push blood back toward 
              the heart, reducing swelling, preventing fluid buildup, and fighting leg fatigue — within 
              minutes of putting them on.
            </p>
            <p className="feature-quote">
              "Whether you're managing varicose veins, edema, or tired legs, proper graduated compression 
              is one of the most effective, low-risk ways to improve circulation," says Nurse Bethany C.
            </p>
          </div>
        </section>

        {/* Feature Section 2 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876aed9de5375a80ca2c3df_IMAGE%202%20-%20PRE%20LANDER.webp" 
              alt="Comfortable design" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">2. Designed for Comfort, Not Constriction</h2>
            <p className="feature-description">
              Tired of socks that dig in, roll down, or cause more pain than they solve? EverSock™ is different.
            </p>
            <p className="feature-description">
              Crafted for all-day wear, they slide on easily and fit calves up to 28 inches, giving a soft, 
              supportive hug that's ideal for:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Diabetics, POTS, lymphedema</li>
              <li><Check className="check-icon" /> Travelers & Flights</li>
              <li><Check className="check-icon" /> Nurses, teachers & retail workers</li>
            </ul>
            <p className="feature-description">
              No pinching. No cutting off circulation. Just soothing compression and breathable fabric 
              that keeps your legs feeling light and supported.
            </p>
          </div>
        </section>

        {/* Feature Section 3 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876afd1da6a4e1a0c0a206a_IMAGE%203%20-%20PRE%20LANDER.webp" 
              alt="Prevent circulatory issues" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">3. Helps Prevent Serious Circulatory Issues</h2>
            <p className="feature-description">
              Don't ignore the signs. Leg swelling and fatigue aren't just uncomfortable — they can be 
              early warnings of serious problems like:
            </p>
            <ul className="alert-list">
              <li><AlertCircle className="alert-icon" /> Blood clots (DVT)</li>
              <li><AlertCircle className="alert-icon" /> Chronic venous insufficiency</li>
              <li><AlertCircle className="alert-icon" /> Varicose veins</li>
            </ul>
            <p className="feature-description">
              Wearing EverSock™ daily supports healthy circulation, helping your body move blood 
              efficiently and reducing the risk of blood pooling and complications.
            </p>
          </div>
        </section>

        {/* Feature Section 4 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876b07475d04aceb35816f8_IMAGE%204%20-%20PRE%20LANDER.webp" 
              alt="Breathable fabric" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">4. Comfortable & Breathable Fabric You Can Actually Live In</h2>
            <p className="feature-description">
              These aren't medical socks you hide under pants. EverSock™ is made from premium, 
              antimicrobial, moisture-wicking materials that:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Resist odor and bacteria</li>
              <li><Check className="check-icon" /> Stay snug without slipping</li>
              <li><Check className="check-icon" /> Withstand frequent washing</li>
              <li><Check className="check-icon" /> Feel good from morning to night</li>
            </ul>
            <p className="feature-description">
              They look sleek enough for professional settings, cozy enough for home, and durable 
              enough for daily use.
            </p>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="testimonial-section">
          <div className="testimonial-container">
            <div className="testimonial-image">
              <img 
                src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876b25d35def86c03da8ac3_BETHANY%20C%205.%20IMAGE%201.webp" 
                alt="Customer testimonial" 
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="testimonial-content">
              <h2 className="testimonial-title">Join Over 50,000+ Customers Who Chose Relief With EverSock™</h2>
              <blockquote className="testimonial-quote">
                "These socks changed everything. No more ankle swelling after long shifts, 
                and I feel so much more energized."
              </blockquote>
              <p className="testimonial-author">-Marie D.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="special-badge">☀️ SWELLING FREE SUMMER SPECIAL 🧦</div>
          
          <div className="cta-container">
            <div className="cta-image">
              <img 
                src="https://cdn.prod.website-files.com/68768b41a5de382a567de441/6876b381a297cb17d1c58c68_FINAL%20IMAGE%20-%20BUY%202%20GET%202%20FREE-min.webp" 
                alt="Buy 2 Get 2 Free" 
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="cta-content">
              <div className="gift-badge">
                <Gift className="gift-icon" /> BUY MORE, SAVE MORE
              </div>
              <h2 className="cta-title">UP TO 50% OFF FOR A LIMITED TIME ONLY</h2>
              <p className="cta-description">
                This limited-time deal is in high demand and stock keeps selling out.
              </p>
              <button className="cta-button">
                GET 50% OFF NOW
                <ArrowRight className="arrow-icon" />
              </button>
              <div className="cta-info">
                <span className="risk-badge">Sell-Out Risk: <span className="high">High</span></span>
                <span className="separator">|</span>
                <span className="free-shipping"><strong>FREE</strong> Shipping</span>
              </div>
              <p className="guarantee">Try it today with a 60-Day Money Back Guarantee!</p>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="footer">
        <h2 className="footer-brand">HEALTH-INSIDER</h2>
      </footer>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default Home;